import Image from 'next/image'
import React, { ComponentProps } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { Box, styled } from '@mui/material'

// Define aspect ratio constants
const ASPECT_RATIOS = {
  horizontal: 16 / 10, // 1.6
  vertical: 0.74,    // 0.8
  square: 1 / 1,     // 1.0
} as const

export type ImageVariant = keyof typeof ASPECT_RATIOS

export type CustomImageProps = Omit<ComponentProps<typeof Box>, 'component' | 'slotProps'> & {
  src: string
  alt: string
  variant?: ImageVariant
  objectFit?: 'cover' | 'contain'
  objectPosition?: string
  borderRadius?: number
  noRatio?: boolean
  loading?: 'lazy' | 'eager'
  priority?: boolean
  srcSet?: string
  sizes?: string
  unoptimized?: boolean
  blurImage?: boolean
  slotProps?: {
    img?: Partial<ComponentProps<typeof Image>>
  }
}

export const CustomImage: React.FC<CustomImageProps> = ({
  src,
  alt,
  srcSet,
  sizes,
  variant = 'square',
  objectFit = 'cover',
  objectPosition,
  borderRadius = 8,
  noRatio = false,
  loading = 'lazy',
  priority = false,
  // Default to Next's image optimization. Override with `unoptimized` for sources
  // already served by an upstream CDN (e.g. Cloudinary) or for SVGs.
  unoptimized = false,
  blurImage = false,
  slotProps,
  ...props
}) => {
  // Get placeholder image based on variant for error cases
  const getPlaceholderImage = () => `/static/product-placeholder-${variant}.jpg`

  // Get object position based on variant or prop
  const getObjectPosition = () => {
    if (objectPosition) return objectPosition

    return variant === 'horizontal' || variant === 'vertical' ? '50% 0%' : 'center'
  }

  const imageContent = (
    <Image
      src={src}
      alt={alt}
      sizes={sizes}
      fill
      unoptimized={unoptimized}
      priority={priority}
      loading={priority ? 'eager' : loading}
      onError={(e) => {
        // Fallback to placeholder on error
        const imgElement = e.currentTarget as HTMLImageElement

        imgElement.src = getPlaceholderImage()
      }}
      style={{
        objectFit: objectFit,
        objectPosition: getObjectPosition(),
        borderRadius: pxToRem(borderRadius),
        filter: blurImage ? 'blur(8px)' : 'none',
      }}
      {...slotProps?.img}
    />
  )

  if (noRatio) {
    return (
      <Container {...props}>
        {imageContent}
      </Container>
    )
  }

  return (
    <AspectRatio
      $ratio={ASPECT_RATIOS[variant]}
      {...props}
    >
      {imageContent}
    </AspectRatio>
  )
}

const Container = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
`

const AspectRatio = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$ratio',
})<{ $ratio: number }>`
  position: relative;
  width: 100%;
  border-radius: inherit;

  &::before {
    content: '';
    display: block;
    width: 100%;
    padding-top: ${({ $ratio }) => `${(1 / $ratio) * 100}%`};
  }

  & > * {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`
